import { shouldShowTrialPrompts, shouldShowUpgradePrompts } from "@/config/features";
import { AGE_RANGES, type ReadingLevel, type Theme } from "@api/types";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import {
    Calendar,
    Check,
    Cookie,
    CreditCard,
    Download,
    Edit2,
    ExternalLink,
    Eye,
    EyeOff,
    FileText,
    HelpCircle,
    Link2,
    Mail,
    Monitor,
    Palette,
    Shield,
    Star,
    Trash2,
    User,
    Volume2,
    X,
} from "lucide-react";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useState } from "react";
import { HexColorPicker } from "react-colorful";
import { AuthRequired } from "../../components/AuthRequired";
import { PageLayout } from "../../components/PageLayout";
import { QuotaStatusCard } from "../../components/QuotaStatusCard";
import { TrialStatusCard } from "../../components/TrialStatusCard";
import { BrushSizeSelector } from "../../components/ui/brush-size-selector";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Checkbox } from "../../components/ui/checkbox";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { useAuth } from "../../contexts/AuthContext";
import { useThemeManager } from "../../hooks/useThemeManager";
import { useToast } from "../../hooks/useToast";
import { useUserAccount } from "../../hooks/useUserAccount";
import { useUserConsents, useUserPreferences } from "../../hooks/useUserPreferences";
import { useUserProfile } from "../../hooks/useUserProfile";
import { apiClient } from "../../lib/api";
import { supabase } from "../../lib/supabase";
import { formatTimeUntil } from "../../lib/utils";
import type { UserPreferences } from "../../types/preferences";

// Extend the Window interface for silktideCookieBannerManager
interface SilktideCookieBannerManager {
    showPreferences: () => void;
}
interface WindowWithSilktide extends Window {
    silktideCookieBannerManager?: SilktideCookieBannerManager;
}

interface SettingsSearch {
    upgrade?: string;
}

export const Route = createFileRoute("/_layout/settings")({
    component: Settings,
    validateSearch: (search: Record<string, unknown>): SettingsSearch => ({
        upgrade: search.upgrade as string | undefined,
    }),
});

const CONFIRMATION_TEXT = "DELETE MY STORIES";

function Settings() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const { toast } = useToast();
    const posthog = usePostHog();
    const { setThemeAndSave } = useThemeManager();
    const { paymentPlan, isPremium, tier, tierStatus, expiresAt } = useUserAccount();
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [confirmationText, setConfirmationText] = useState("");
    const [isLoadingPortal, setIsLoadingPortal] = useState(false);

    // Get search params for upgrade status
    const { upgrade } = Route.useSearch();

    // User preferences and consents
    const { preferences, isLoading: preferencesLoading, updatePreferences, isUpdating } = useUserPreferences();

    const { consents, updateConsent, isUpdating: consentUpdating } = useUserConsents();

    // User profile
    const {
        profile,
        isLoading: profileLoading,
        updateProfile,
        isUpdating: profileUpdating,
        getEffectiveDisplayName,
    } = useUserProfile();

    // Local state for color picker
    const [showColorPicker, setShowColorPicker] = useState(false);

    // Save feedback state
    const [savedSettings, setSavedSettings] = useState<Set<string>>(new Set());

    // Display name editing state
    const [isEditingDisplayName, setIsEditingDisplayName] = useState(false);
    const [editDisplayName, setEditDisplayName] = useState("");

    // Track settings page visit
    useEffect(() => {
        if (user && !loading) {
            posthog?.capture("settings_page_visited");
        }
    }, [user, loading, posthog]);

    // Handle upgrade success notifications
    useEffect(() => {
        if (upgrade === "success") {
            toast({
                title: "Upgrade successful! 🎉",
                description: "Your account has been upgraded. Enjoy your new features!",
                variant: "success",
            });
            // Clear the URL parameter
            navigate({ to: "/settings", search: { upgrade: undefined }, replace: true });
        } else if (upgrade === "canceled") {
            toast({
                title: "Upgrade canceled",
                description: "Your upgrade was canceled. No charges were made.",
                variant: "default",
            });
            // Clear the URL parameter
            navigate({ to: "/settings", search: { upgrade: undefined }, replace: true });
        }
    }, [upgrade, toast, navigate]);

    // Helper function to show save feedback
    const showSaveFeedback = useCallback((settingKey: string) => {
        setSavedSettings((prev) => new Set(prev).add(settingKey));
        setTimeout(() => {
            setSavedSettings((prev) => {
                const newSet = new Set(prev);
                newSet.delete(settingKey);
                return newSet;
            });
        }, 1000);
    }, []);

    // SavedIndicator component
    const SavedIndicator = ({ show }: { show: boolean }) => (
        <div
            className={`w-4 h-4 ml-2 rounded-full transition-all duration-300 flex items-center justify-center ${
                show ? "opacity-100 bg-success" : "opacity-0 bg-transparent"
            }`}
        >
            {show && (
                <svg className="w-2 h-2 text-white" fill="currentColor" viewBox="0 0 20 20">
                    <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                    />
                </svg>
            )}
        </div>
    );

    // Helper functions for preference updates
    const handleVoiceToggle = (checked: boolean) => {
        if (!preferences) return;

        const updatedPreferences: UserPreferences = {
            canvas: preferences.canvas,
            display: {
                theme: preferences.display.theme as UserPreferences["display"]["theme"],
                showVoiceButton: checked,
            },
        };

        updatePreferences(updatedPreferences);
        showSaveFeedback("voice");

        posthog?.capture("settings_voice_button_toggled", {
            enabled: checked,
            previous_state: preferences?.display?.showVoiceButton,
        });
    };

    const handleThemeChange = (theme: Theme) => {
        if (!preferences) return;

        // Update theme context and save to database
        setThemeAndSave(theme);
        showSaveFeedback("theme");

        posthog?.capture("settings_theme_changed", {
            theme: theme,
            previous_theme: preferences?.display?.theme,
        });
    };

    const handleBrushSizeChange = (size: number) => {
        if (!preferences) return;

        const newSize = Math.round(size);
        const updatedPreferences: UserPreferences = {
            display: preferences.display as UserPreferences["display"],
            canvas: {
                defaultBrushColor: preferences.canvas.defaultBrushColor,
                defaultBrushSize: newSize,
            },
        };

        updatePreferences(updatedPreferences);
        showSaveFeedback("brushSize");

        posthog?.capture("settings_brush_size_changed", {
            size: newSize,
            previous_size: preferences?.canvas?.defaultBrushSize,
        });
    };

    const handleColorChange = (color: string) => {
        if (!preferences) return;

        const updatedPreferences: UserPreferences = {
            display: preferences.display,
            canvas: {
                defaultBrushSize: preferences.canvas.defaultBrushSize,
                defaultBrushColor: color,
            },
        };

        updatePreferences(updatedPreferences);
        showSaveFeedback("brushColor");

        posthog?.capture("settings_color_changed", {
            color: color,
            previous_color: preferences?.canvas?.defaultBrushColor,
        });
    };

    const handleMarketingConsent = (checked: boolean) => {
        updateConsent({ consentType: "marketing", consented: checked });
        showSaveFeedback("marketing");

        posthog?.capture("settings_marketing_consent_changed", {
            consented: checked,
            previous_consent: consents?.marketing?.consented,
        });
    };

    const handleAgeRangeChange = (ageRangeKey: string) => {
        updateProfile({ age_range_key: ageRangeKey });
        showSaveFeedback("ageRange");

        posthog?.capture("settings_age_range_changed", {
            age_range: ageRangeKey,
            previous_age_range: profile?.age_range_key,
        });
    };

    const handleReadingLevelChange = (readingLevel: ReadingLevel) => {
        updateProfile({ reading_level: readingLevel });
        showSaveFeedback("readingLevel");

        posthog?.capture("settings_reading_level_changed", {
            reading_level: readingLevel,
            previous_reading_level: profile?.reading_level,
        });
    };

    // Display name edit handlers
    const handleEditDisplayName = () => {
        setEditDisplayName(profile?.display_name || "");
        setIsEditingDisplayName(true);
    };

    const handleSaveDisplayName = () => {
        if (editDisplayName !== profile?.display_name) {
            updateProfile({ display_name: editDisplayName });
            showSaveFeedback("displayName");

            posthog?.capture("settings_display_name_changed", {
                has_display_name: editDisplayName.length > 0,
                previous_has_display_name: (profile?.display_name?.length || 0) > 0,
            });
        }
        setIsEditingDisplayName(false);
    };

    const handleCancelDisplayName = () => {
        setEditDisplayName("");
        setIsEditingDisplayName(false);
    };

    const handlePasswordReset = async () => {
        if (!user?.email) return;

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(user.email, {
                redirectTo: `${window.location.origin}/reset-password`,
            });

            if (error) throw error;

            toast({
                title: "Password Reset Email Sent",
                description: "Check your email for password reset instructions.",
                variant: "success",
            });

            posthog?.capture("settings_password_reset_requested", {
                email: user.email,
            });
        } catch (error) {
            console.error("Password reset error:", error);
            toast({
                title: "Error",
                description: "Failed to send password reset email. Please try again.",
                variant: "destructive",
            });
        }
    };

    const handleDeleteAccount = async () => {
        if (!user) return;

        setIsDeletingAccount(true);
        try {
            // Delete all user data through API
            await apiClient.deleteAllUserData();

            toast({
                title: "Stories deleted",
                description:
                    "All your stories and data have been permanently deleted. You can create new stories anytime.",
                variant: "success",
            });

            // Navigate to home page
            navigate({ to: "/" });
        } catch (error) {
            console.error("Error deleting user data:", error);
            toast({
                title: "Error",
                description: "Failed to delete your stories. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsDeletingAccount(false);
            setShowDeleteConfirmation(false);
            setConfirmationText("");
        }
    };

    // Helper function to get sign-in provider info
    const getSignInProvider = () => {
        if (!user?.identities || user.identities.length === 0) {
            return { provider: "email", displayName: "Email Sign-in" };
        }

        const identity = user.identities[0]; // Get the primary identity
        switch (identity.provider) {
            case "google":
                return { provider: "google", displayName: "Google" };
            default:
                return { provider: "email", displayName: "Email Sign-in" };
        }
    };

    const signInProvider = getSignInProvider();

    // Helper function for billing card content
    const getBillingCardContent = () => {
        const expiryDate = expiresAt ? new Date(expiresAt) : null;
        const isExpired = expiryDate ? expiryDate <= new Date() : false;

        if (tierStatus === "canceled" && !isExpired) {
            return {
                description: `Your subscription is canceled and will end ${formatTimeUntil(expiresAt!)}. Access your billing portal to reactivate or manage your subscription.`,
                buttonText: "Manage Subscription",
            };
        }

        if (isExpired) {
            return {
                description: `Your subscription expired${expiryDate ? ` on ${expiryDate.toLocaleDateString()}` : ""}. Reactivate to regain access to premium features.`,
                buttonText: "Reactivate Subscription",
            };
        }

        return {
            description: "Manage your subscription and billing information",
            buttonText: "Manage",
        };
    };

    // Helper function to render provider icon
    const renderProviderIcon = () => {
        switch (signInProvider.provider) {
            case "google":
                return <div className="h-4 w-4 bg-gradient-to-r from-blue-500 to-red-500 rounded-full"></div>;
            default:
                return <Mail className="h-4 w-4 text-muted-foreground" />;
        }
    };

    const exportData = async () => {
        if (!user) return;

        try {
            const data = await apiClient.exportUserData();

            // Create timestamp for filename: YYYY-MM-DD-HHMMSS
            const now = new Date();
            const timestamp = now
                .toISOString()
                .replace(/:/g, "") // Remove colons
                .replace(/\./g, "") // Remove dots
                .replace("T", "-") // Replace T with dash
                .slice(0, 15); // Keep YYYY-MM-DD-HHMMSS format

            const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `storydoodle-export-${timestamp}.json`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            toast({
                title: "Data exported",
                description: "Your data has been downloaded as a JSON file.",
                variant: "success",
            });
        } catch (error) {
            console.error("Error exporting data:", error);
            toast({
                title: "Error",
                description: "Failed to export data. Please try again.",
                variant: "destructive",
            });
        }
    };

    const handleCookiePreferences = () => {
        // Trigger Silktide cookie preferences modal
        if (typeof window !== "undefined" && (window as WindowWithSilktide).silktideCookieBannerManager) {
            (window as WindowWithSilktide).silktideCookieBannerManager!.showPreferences();

            posthog?.capture("cookie_preferences_opened", {
                source: "settings_page",
            });
        }
    };

    const handleManageBilling = async () => {
        if (paymentPlan !== "subscription") return;

        setIsLoadingPortal(true);
        try {
            const { portal_url } = await apiClient.getCustomerPortalUrl();

            // Open portal in new tab for better UX
            window.open(portal_url, "_blank", "noopener,noreferrer");

            posthog?.capture("billing_portal_accessed");
        } catch (error) {
            console.error("Error accessing billing portal:", error);
            toast({
                title: "Error",
                description: "Failed to access billing portal. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsLoadingPortal(false);
        }
    };

    // Personal Profile card sub-render methods
    const renderPersonalProfileLoadingSkeleton = () => (
        <div className="space-y-6">
            {/* Display name loading skeleton */}
            <div className="flex items-center justify-between">
                <div>
                    <div className="h-4 w-24 bg-muted animate-pulse rounded"></div>
                    <div className="h-3 w-32 bg-muted animate-pulse rounded mt-1"></div>
                </div>
                <div className="h-8 w-32 bg-muted animate-pulse rounded"></div>
            </div>

            {/* Settings controls loading skeleton */}
            <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="h-4 w-20 bg-muted animate-pulse rounded"></div>
                        <div className="h-3 w-36 bg-muted animate-pulse rounded mt-1"></div>
                    </div>
                    <div className="h-8 w-32 bg-muted animate-pulse rounded"></div>
                </div>
            </div>

            <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="h-4 w-24 bg-muted animate-pulse rounded"></div>
                        <div className="h-3 w-40 bg-muted animate-pulse rounded mt-1"></div>
                    </div>
                    <div className="h-8 w-40 bg-muted animate-pulse rounded"></div>
                </div>
            </div>
        </div>
    );

    const renderDisplayNameField = () => (
        <div className="flex items-center justify-between">
            <div className="flex items-center">
                <div>
                    <label className="text-sm font-medium text-foreground">Display Name</label>
                    <p className="text-xs text-muted-foreground">How you'd like to be addressed</p>
                </div>
                <SavedIndicator show={savedSettings.has("displayName")} />
            </div>

            {isEditingDisplayName ? (
                <div className="flex items-center gap-2">
                    <Input
                        value={editDisplayName}
                        onChange={(e) => setEditDisplayName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                handleSaveDisplayName();
                            } else if (e.key === "Escape") {
                                handleCancelDisplayName();
                            }
                        }}
                        placeholder={getEffectiveDisplayName(user, profile)}
                        className="w-32"
                        disabled={profileUpdating}
                        autoFocus
                    />
                    <Button
                        onClick={handleSaveDisplayName}
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-success"
                        disabled={profileUpdating}
                    >
                        <Check className="h-3 w-3" />
                    </Button>
                    <Button
                        onClick={handleCancelDisplayName}
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive hover:text-destructive"
                        disabled={profileUpdating}
                    >
                        <X className="h-3 w-3" />
                    </Button>
                </div>
            ) : (
                <div className="flex items-center gap-2">
                    <span className="text-sm text-foreground min-w-0 truncate">
                        {profile?.display_name || getEffectiveDisplayName(user, profile)}
                    </span>
                    <Button
                        onClick={handleEditDisplayName}
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        disabled={profileLoading}
                    >
                        <Edit2 className="h-3 w-3" />
                    </Button>
                </div>
            )}
        </div>
    );

    const renderPremiumProfileControls = () => (
        <>
            <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <div>
                            <label className="text-sm font-medium text-foreground">Age Range</label>
                            <p className="text-xs text-muted-foreground">Helps create age-appropriate stories</p>
                        </div>
                        <SavedIndicator show={savedSettings.has("ageRange")} />
                    </div>
                    <Select
                        value={profile?.age_range_key || ""}
                        onValueChange={handleAgeRangeChange}
                        disabled={profileLoading || profileUpdating}
                    >
                        <SelectTrigger className="w-32 cursor-pointer">
                            <SelectValue placeholder="Select age" />
                        </SelectTrigger>
                        <SelectContent>
                            {AGE_RANGES.map((range) => (
                                <SelectItem key={range.value} value={range.value}>
                                    {range.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center">
                        <div>
                            <label className="text-sm font-medium text-foreground">Reading Level</label>
                            <p className="text-xs text-muted-foreground">Adjust story complexity and vocabulary</p>
                        </div>
                        <SavedIndicator show={savedSettings.has("readingLevel")} />
                    </div>
                    <Select
                        value={profile?.reading_level || "age-appropriate"}
                        onValueChange={handleReadingLevelChange}
                        disabled={profileLoading || profileUpdating}
                    >
                        <SelectTrigger className="min-w-[160px] cursor-pointer">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="beginner">Beginner</SelectItem>
                            <SelectItem value="age-appropriate">Age Appropriate</SelectItem>
                            <SelectItem value="advanced">Advanced</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {tier === "plus" && paymentPlan === "trial" && (
                <div className="border-t pt-4">
                    <div className="text-xs text-muted-foreground bg-muted/50 rounded-lg p-3">
                        <Star className="h-3 w-3 inline mr-1 text-secondary" />
                        Keep your age range and reading level settings after your trial with Plus
                    </div>
                </div>
            )}
        </>
    );

    const renderUpgradeTeaser = () =>
        shouldShowUpgradePrompts() && (
            <div className="border-t pt-4">
                <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
                    <div className="flex items-center gap-2">
                        <Star className="h-4 w-4 text-secondary" />
                        <h4 className="text-sm font-medium text-secondary">Personalized Stories</h4>
                        <span className="text-xs bg-secondary/20 text-secondary px-2 py-0.5 rounded-full">PLUS</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                        Unlock personalized story experiences with custom age ranges and reading levels.
                    </p>
                    <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
                        <span>• Age ranges: 3-5, 8-10, 11-13, 13+</span>
                        <span>• Reading levels: Beginner to Advanced</span>
                    </div>
                    <div className="pt-2">
                        <p className="text-sm font-bold">See billing options below to upgrade</p>
                    </div>
                </div>
            </div>
        );

    const renderPersonalProfileContent = () => (
        <>
            {renderDisplayNameField()}
            {isPremium ? renderPremiumProfileControls() : renderUpgradeTeaser()}
        </>
    );

    // Show loading state while auth is being determined
    if (loading) {
        return (
            <PageLayout title="Settings" breadcrumbs={[{ label: "Home", to: "/" }, { label: "Settings" }]}>
                <div className="flex items-center justify-center min-h-96">
                    <div className="p-6 max-w-sm">
                        <div className="text-center space-y-4">
                            <div className="h-8 w-32 bg-muted animate-pulse rounded mx-auto"></div>
                            <div className="h-4 w-48 bg-muted animate-pulse rounded mx-auto"></div>
                            <div className="h-10 w-24 bg-muted animate-pulse rounded mx-auto"></div>
                        </div>
                    </div>
                </div>
            </PageLayout>
        );
    }

    if (!user) {
        return (
            <PageLayout title="Settings" breadcrumbs={[{ label: "Home", to: "/" }, { label: "Settings" }]}>
                <AuthRequired description="Please sign in to access account settings." className="p-6 max-w-sm" />
            </PageLayout>
        );
    }

    return (
        <PageLayout title="Settings" breadcrumbs={[{ label: "Home", to: "/" }, { label: "Settings" }]}>
            <main className="container mx-auto px-4 sm:px-6 py-8 max-w-2xl">
                <div className="space-y-6">
                    {/* Account Information */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Account Information
                            </CardTitle>
                            <CardDescription>Your account details and profile information</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="flex items-center gap-3">
                                    <Mail className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">Email</label>
                                        <p className="text-foreground">{user.email}</p>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <Calendar className="h-4 w-4 text-muted-foreground" />
                                    <div>
                                        <label className="text-sm font-medium text-muted-foreground">
                                            Member Since
                                        </label>
                                        <p className="text-foreground">
                                            {new Date(user.created_at).toLocaleDateString()}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <h4 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                                    <Link2 className="h-4 w-4" />
                                    Account Connections
                                </h4>
                                <div className="space-y-3">
                                    <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            {renderProviderIcon()}
                                            <span className="text-sm">{signInProvider.displayName}</span>
                                        </div>
                                        <span className="text-xs text-green-700 bg-green-50 dark:bg-green-950/50 dark:text-green-300 px-2 py-1 rounded-full">
                                            Connected
                                        </span>
                                    </div>
                                </div>
                            </div>
                            {signInProvider.provider === "email" && (
                                <div className="border-t pt-4">
                                    <div className="space-y-3">
                                        <Button
                                            onClick={handlePasswordReset}
                                            variant="outline"
                                            className="w-full sm:w-auto"
                                        >
                                            <Shield className="h-4 w-4 mr-2" />
                                            Reset Password
                                        </Button>
                                        <p className="text-xs text-muted-foreground">
                                            Send a password reset email to {user.email}
                                        </p>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {/* Personal Profile */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <User className="h-5 w-5" />
                                Personal Profile
                            </CardTitle>
                            <CardDescription>
                                Customize your story experience with personalized settings
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {profileLoading ? renderPersonalProfileLoadingSkeleton() : renderPersonalProfileContent()}
                        </CardContent>
                    </Card>

                    {shouldShowTrialPrompts() && <TrialStatusCard />}

                    <QuotaStatusCard />

                    {paymentPlan === "subscription" &&
                        (() => {
                            const cardContent = getBillingCardContent();
                            return (
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center gap-2">
                                            <CreditCard className="h-5 w-5" />
                                            Billing Management
                                        </CardTitle>
                                        <CardDescription>{cardContent.description}</CardDescription>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="space-y-3">
                                            <Button
                                                onClick={handleManageBilling}
                                                disabled={isLoadingPortal}
                                                className="w-full sm:w-auto"
                                                variant="outline"
                                            >
                                                <ExternalLink className="h-4 w-4 mr-2" />
                                                {isLoadingPortal ? "Opening Portal..." : cardContent.buttonText}
                                            </Button>
                                            <p className="text-xs text-muted-foreground">
                                                Access your billing portal to update payment methods, view invoices, and
                                                manage your subscription.
                                            </p>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })()}

                    {/* Display Settings */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Monitor className="h-5 w-5" />
                                Display Settings
                            </CardTitle>
                            <CardDescription>Customize your app appearance and preferences</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center">
                                    <div>
                                        <label className="text-sm font-medium text-foreground">Theme</label>
                                        <p className="text-xs text-muted-foreground">
                                            Choose your preferred color theme
                                        </p>
                                    </div>
                                    <SavedIndicator show={savedSettings.has("theme")} />
                                </div>
                                <Select
                                    value={preferences?.display?.theme ?? "system"}
                                    onValueChange={(value: Theme) => handleThemeChange(value)}
                                    disabled={preferencesLoading || isUpdating}
                                >
                                    <SelectTrigger className="w-32 cursor-pointer">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="light">Light</SelectItem>
                                        <SelectItem value="dark">Dark</SelectItem>
                                        <SelectItem value="system">System</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="border-t pt-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div>
                                            <label className="text-sm font-medium text-foreground">Voice Buttons</label>
                                            <p className="text-xs text-muted-foreground">Show text-to-speech buttons</p>
                                        </div>
                                        <SavedIndicator show={savedSettings.has("voice")} />
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Volume2 className="h-4 w-4 text-primary" />
                                        {preferences?.display?.showVoiceButton ? (
                                            <Eye className="h-4 w-4 text-muted-foreground" />
                                        ) : (
                                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                                        )}
                                        <Checkbox
                                            checked={preferences?.display?.showVoiceButton ?? true}
                                            onCheckedChange={handleVoiceToggle}
                                            disabled={preferencesLoading || isUpdating}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <h4 className="text-sm font-medium text-foreground mb-4 flex items-center gap-2">
                                    <Palette className="h-4 w-4" />
                                    Drawing Preferences
                                </h4>

                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center">
                                            <div>
                                                <label className="text-sm font-medium text-foreground">
                                                    Default Brush Size
                                                </label>
                                                <p className="text-xs text-muted-foreground">
                                                    Initial brush size for new drawings
                                                </p>
                                            </div>
                                            <SavedIndicator show={savedSettings.has("brushSize")} />
                                        </div>
                                        <BrushSizeSelector
                                            value={preferences?.canvas?.defaultBrushSize ?? 3}
                                            onChange={handleBrushSizeChange}
                                            disabled={preferencesLoading || isUpdating}
                                        />
                                    </div>

                                    <div className="border-t pt-4">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center">
                                                <div>
                                                    <label className="text-sm font-medium text-foreground">
                                                        Default Brush Color
                                                    </label>
                                                    <p className="text-xs text-muted-foreground">
                                                        Initial color for new drawings
                                                    </p>
                                                </div>
                                                <SavedIndicator show={savedSettings.has("brushColor")} />
                                            </div>
                                            <div className="flex items-center gap-3">
                                                <div
                                                    className="w-8 h-8 rounded border-2 border-border cursor-pointer"
                                                    style={{
                                                        backgroundColor:
                                                            preferences?.canvas?.defaultBrushColor ?? "#000000",
                                                    }}
                                                    onClick={() => setShowColorPicker(!showColorPicker)}
                                                />
                                                <Input
                                                    value={preferences?.canvas?.defaultBrushColor ?? "#000000"}
                                                    onChange={(e) => handleColorChange(e.target.value)}
                                                    placeholder="#000000"
                                                    className="w-24 text-sm"
                                                    disabled={preferencesLoading || isUpdating}
                                                />
                                            </div>
                                        </div>

                                        {showColorPicker && (
                                            <div className="mt-3 p-3 border rounded-lg bg-background">
                                                <div className="flex flex-col items-center space-y-3">
                                                    <HexColorPicker
                                                        color={preferences?.canvas?.defaultBrushColor ?? "#000000"}
                                                        onChange={handleColorChange}
                                                    />
                                                    <Button
                                                        variant="outline"
                                                        size="sm"
                                                        onClick={() => setShowColorPicker(false)}
                                                    >
                                                        Close
                                                    </Button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Privacy & Data */}
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Shield className="h-5 w-5" />
                                Privacy & Data
                            </CardTitle>
                            <CardDescription>Manage your personal data and privacy settings</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="space-y-3">
                                <Button onClick={exportData} variant="outline" className="w-full sm:w-auto">
                                    <Download className="h-4 w-4 mr-2" />
                                    Export My Data
                                </Button>
                                <p className="text-xs text-muted-foreground">
                                    Download a copy of all your stories, drawings, and account information.
                                </p>
                            </div>

                            <div className="border-t pt-4">
                                <div className="space-y-3">
                                    <Button
                                        onClick={handleCookiePreferences}
                                        variant="outline"
                                        className="w-full sm:w-auto"
                                    >
                                        <Cookie className="h-4 w-4 mr-2" />
                                        Cookie Preferences
                                    </Button>
                                    <p className="text-xs text-muted-foreground">Manage your cookie preferences.</p>
                                </div>
                            </div>

                            <div className="border-t pt-4">
                                <h4 className="font-medium text-foreground mb-3">Communication Preferences</h4>
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center">
                                        <div>
                                            <label className="text-sm font-medium text-foreground">
                                                Marketing Emails
                                            </label>
                                            <p className="text-xs text-muted-foreground">
                                                Receive emails about StoryDoodle updates and new features
                                            </p>
                                        </div>
                                        <SavedIndicator show={savedSettings.has("marketing")} />
                                    </div>
                                    <Checkbox
                                        checked={consents?.marketing?.consented ?? false}
                                        onCheckedChange={handleMarketingConsent}
                                        disabled={consentUpdating}
                                    />
                                </div>
                                {consents?.marketing?.date && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Last updated: {new Date(consents.marketing.date).toLocaleDateString()}
                                    </p>
                                )}
                            </div>

                            <div className="border-t pt-4">
                                <h4 className="font-medium text-foreground mb-2">Data Retention</h4>
                                <p className="text-sm text-muted-foreground">
                                    You may delete your account data at any time from your account settings. Individual
                                    stories can be deleted at any time from the My Stories page.
                                </p>
                            </div>

                            <div className="border-t pt-4">
                                <h4 className="font-medium text-foreground mb-2">Parent Rights</h4>
                                <p className="text-sm text-muted-foreground">
                                    Parents have full control over accounts used with their children, including the
                                    ability to review and delete all content and data.
                                </p>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Legal & Support */}
                    <Card className="md:hidden">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <HelpCircle className="h-5 w-5" />
                                Legal & Support
                            </CardTitle>
                            <CardDescription>Access help resources and legal information</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                <Link
                                    to="/help"
                                    className="flex items-center gap-2 p-3 rounded-lg border border-border hover:bg-muted transition-colors"
                                >
                                    <HelpCircle className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm font-medium">Help</span>
                                </Link>
                                <Link
                                    to="/privacy"
                                    className="flex items-center gap-2 p-3 rounded-lg border border-border hover:bg-muted transition-colors"
                                >
                                    <Shield className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm font-medium">Privacy Policy</span>
                                </Link>
                                <Link
                                    to="/terms"
                                    className="flex items-center gap-2 p-3 rounded-lg border border-border hover:bg-muted transition-colors"
                                >
                                    <FileText className="h-4 w-4 text-muted-foreground" />
                                    <span className="text-sm font-medium">Terms of Service</span>
                                </Link>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Danger Zone */}
                    <Card className="border-destructive">
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2 text-destructive">
                                <Trash2 className="h-5 w-5" />
                                Delete All Stories
                            </CardTitle>
                            <CardDescription>Permanently delete all your stories and drawings</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-4">
                            {!showDeleteConfirmation ? (
                                <div className="space-y-3">
                                    <Button
                                        onClick={() => setShowDeleteConfirmation(true)}
                                        variant="destructive"
                                        className="w-full sm:w-auto"
                                    >
                                        Delete All My Stories
                                    </Button>
                                    <p className="text-xs text-muted-foreground">
                                        This action cannot be undone. All your stories and drawings will be permanently
                                        deleted. You can still use your account to create new stories.
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-4 p-4 bg-destructive/10 border border-destructive rounded-md">
                                    <div>
                                        <h4 className="font-medium text-destructive">Are you sure?</h4>
                                        <p className="text-sm text-muted-foreground mt-1">
                                            This will delete all your stories and drawings forever. This cannot be
                                            undone.
                                        </p>
                                    </div>
                                    <div className="space-y-3">
                                        <div>
                                            <label className="text-sm font-medium text-destructive mb-2 block">
                                                Type {CONFIRMATION_TEXT} to confirm:
                                            </label>
                                            <Input
                                                value={confirmationText}
                                                onChange={(e) => setConfirmationText(e.target.value)}
                                                placeholder={CONFIRMATION_TEXT}
                                                className="border-destructive focus-visible:ring-destructive"
                                            />
                                        </div>
                                        <div className="flex gap-3 justify-end">
                                            <Button
                                                onClick={() => {
                                                    setShowDeleteConfirmation(false);
                                                    setConfirmationText("");
                                                }}
                                                variant="outline"
                                                size="sm"
                                            >
                                                Cancel
                                            </Button>
                                            <Button
                                                onClick={handleDeleteAccount}
                                                disabled={isDeletingAccount || confirmationText !== CONFIRMATION_TEXT}
                                                variant="destructive"
                                                size="sm"
                                            >
                                                {isDeletingAccount ? "Deleting..." : "Delete all my stories"}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </main>
        </PageLayout>
    );
}
